import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Save, 
  Edit,
  VideoFile,
  Article,
  Work
} from '@mui/icons-material';
import { MediaPreview } from './components/MediaPreview';
import { MediaManager } from './components/MediaManager';
import { PortfolioContentItem, PortfolioTab } from '../data/portfolioData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`portfolio-tabpanel-${index}`}
      aria-labelledby={`portfolio-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const PortfolioPageEditor: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [portfolioTabs, setPortfolioTabs] = useState<PortfolioTab[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // 편집 다이얼로그 상태
  const [editingContent, setEditingContent] = useState<PortfolioContentItem | null>(null);
  const [showContentDialog, setShowContentDialog] = useState(false);
  
  // 임시 업로드 관리
  const [tempUploads, setTempUploads] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadPortfolioData();
  }, []);

  // 편집 중인 콘텐츠 상태 디버깅
  useEffect(() => {
    if (editingContent) {
      console.log('현재 편집 중인 콘텐츠:', editingContent);
    }
  }, [editingContent]);

  const loadPortfolioData = async () => {
    setLoading(true);
    try {
      const { portfolioApi } = await import('../services/api');
      const data = await portfolioApi.getAll();
      setPortfolioTabs(data || []);
    } catch (error) {
      console.error('포트폴리오 데이터 로드 실패:', error);
      setErrorMessage('포트폴리오 데이터를 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { portfolioApi } = await import('../services/api');
      await portfolioApi.savePortfolioData(portfolioTabs);
      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };



  const handleAddVideo = () => {
    // 영상 콘텐츠를 contents 배열에 추가하는 방식으로 변경
    const newContent: PortfolioContentItem = {
      id: Date.now(),
      subTab: '',
      title: '',
      description: '',
      src: ''
    };
    setEditingContent(newContent);
    setShowContentDialog(true);
  };

  const handleEditVideo = (content: PortfolioContentItem) => {
    setEditingContent(content);
    setShowContentDialog(true);
  };

  const handleDeleteVideo = (contentId: number) => {
    setPortfolioTabs(prev => prev.map((tab, i) => 
      i === tabValue ? {
        ...tab,
        contents: tab.contents.filter(c => c.id !== contentId)
      } : tab
    ));
  };



  const handleAddContent = () => {
    const newContent: PortfolioContentItem = {
      id: Date.now(),
      subTab: '',
      title: '',
      description: '',
      src: ''
    };
    setEditingContent(newContent);
    setShowContentDialog(true);
  };

  const handleEditContent = (content: PortfolioContentItem) => {
    console.log('편집할 콘텐츠:', content);
    setEditingContent(content);
    setShowContentDialog(true);
  };

  const handleDeleteContent = (contentId: number) => {
    setPortfolioTabs(prev => prev.map((tab, i) => 
      i === tabValue ? {
        ...tab,
        contents: tab.contents.filter(c => c.id !== contentId)
      } : tab
    ));
  };

  const handleSaveContent = () => {
    if (editingContent && portfolioTabs[tabValue]) {
      // 제목이 비어있으면 기본값 설정
      if (!editingContent.title.trim()) {
        setEditingContent({ ...editingContent, title: '제목 없음' });
      }
      
      const currentTab = portfolioTabs[tabValue];
      const existingIndex = currentTab.contents.findIndex(c => c.id === editingContent.id);
      
      const updatedContents = existingIndex >= 0
        ? currentTab.contents.map(c => c.id === editingContent.id ? editingContent : c)
        : [...currentTab.contents, editingContent];

      setPortfolioTabs(prev => prev.map((tab, i) => 
        i === tabValue ? { ...tab, contents: updatedContents } : tab
      ));
      
      // 저장 완료 - 임시 업로드는 유지 (파일 삭제하지 않음)
    }
    setShowContentDialog(false);
    setEditingContent(null);
  };

  const handleCancelContent = () => {
    // 임시 업로드된 파일들 정리
    Object.values(tempUploads).forEach(async (fileUrl) => {
      try {
        const urlParts = fileUrl.split('/');
        const type = urlParts[urlParts.length - 2]; // image 또는 video
        const filename = urlParts[urlParts.length - 1];
        
        const { uploadApi } = await import('../services/api');
        await uploadApi.deleteFile(type, filename);
      } catch (error) {
        console.error('임시 파일 삭제 실패:', error);
      }
    });
    
    // 임시 업로드 상태 초기화
    setTempUploads({});
    setEditingContent(null);
    setShowContentDialog(false);
  };

  // 임시 업로드 처리
  const handleTempUpload = (media: any) => {
    if (!editingContent) return;
    setTempUploads(prev => ({ ...prev, src: media.url }));
  };

  // 임시 업로드 취소 처리
  const handleCancelUpload = async (mediaId: string) => {
    const fileUrl = tempUploads.src;
    
    if (fileUrl) {
      try {
        const urlParts = fileUrl.split('/');
        const type = urlParts[urlParts.length - 2];
        const filename = urlParts[urlParts.length - 1];
        
        const { uploadApi } = await import('../services/api');
        await uploadApi.deleteFile(type, filename);
        
        // 임시 업로드에서 제거
        const newTempUploads = { ...tempUploads };
        delete newTempUploads.src;
        setTempUploads(newTempUploads);
        
        // 편집 중인 콘텐츠에서도 제거
        if (editingContent) {
          setEditingContent({ ...editingContent, src: '' });
        }
      } catch (error) {
        console.error('임시 업로드 취소 실패:', error);
      }
    }
  };

  // 영상과 이미지 콘텐츠를 분리하는 함수
  const separateContentByType = (contents: PortfolioContentItem[]) => {
    const videos: PortfolioContentItem[] = [];
    const images: PortfolioContentItem[] = [];
    
    contents.forEach(content => {
      if (content.src) {
        const extension = content.src.split('.').pop()?.toLowerCase();
        if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
          videos.push(content);
        } else {
          images.push(content);
        }
      } else {
        // src가 없는 경우 기본적으로 이미지로 분류
        images.push(content);
      }
    });
    
    return { videos, images };
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          포트폴리오 데이터를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Work sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              포트폴리오 페이지 관리
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            포트폴리오 페이지의 콘텐츠를 관리합니다. 각 탭에는 영상과 콘텐츠를 추가할 수 있습니다.
          </Alert>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              {portfolioTabs.map((tab, index) => (
                <Tab key={tab.contentId} label={tab.label} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6">
              {portfolioTabs[tabValue]?.label || '포트폴리오 탭'} 관리
            </Typography>
          </Box>

          {portfolioTabs[tabValue] && (
            <Grid container spacing={3}>
              {/* 영상 섹션 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <VideoFile sx={{ mr: 1 }} />
                        영상 콘텐츠 ({portfolioTabs[tabValue].videos.length + separateContentByType(portfolioTabs[tabValue].contents).videos.length}개)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddVideo}
                      >
                        영상 추가
                      </Button>
                    </Box>
                    
                    <List>
                      {/* 기존 videos 배열의 영상들 */}
                      {portfolioTabs[tabValue].videos.map((video) => (
                        <ListItem key={`video-${video.id}`} divider>
                          <ListItemText
                            primary={video.title || '제목 없음'}
                            secondary={video.description || '설명 없음'}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditVideo(video)}
                              sx={{ mr: 1 }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleDeleteVideo(video.id)}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                      
                      {/* contents 배열에서 영상으로 분류된 항목들 */}
                      {separateContentByType(portfolioTabs[tabValue].contents).videos.map((content) => (
                        <ListItem key={`content-video-${content.id}`} divider>
                          <ListItemText
                            primary={content.title || '제목 없음'}
                            secondary={
                              <Box>
                                <Chip label={content.subTab || '서브탭 없음'} size="small" sx={{ mr: 1 }} />
                                {content.description || '설명 없음'}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditContent(content)}
                              sx={{ mr: 1 }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleDeleteContent(content.id)}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* 이미지 콘텐츠 섹션 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Article sx={{ mr: 1 }} />
                        이미지 콘텐츠 ({separateContentByType(portfolioTabs[tabValue].contents).images.length}개)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddContent}
                      >
                        이미지 추가
                      </Button>
                    </Box>
                    
                    <List>
                      {separateContentByType(portfolioTabs[tabValue].contents).images.map((content) => (
                        <ListItem key={`content-image-${content.id}`} divider>
                          <ListItemText
                            primary={content.title || '제목 없음'}
                            secondary={
                              <Box>
                                <Chip label={content.subTab || '서브탭 없음'} size="small" sx={{ mr: 1 }} />
                                {content.description || '설명 없음'}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditContent(content)}
                              sx={{ mr: 1 }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleDeleteContent(content.id)}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              size="large"
              sx={{ minWidth: 120 }}
            >
              {saving ? '저장 중...' : '저장'}
            </Button>
          </Box>
        </Paper>





        {/* 콘텐츠 편집 다이얼로그 */}
        <Dialog 
          open={showContentDialog} 
          onClose={() => setShowContentDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingContent?.id ? '미디어 콘텐츠 편집' : '새 미디어 콘텐츠 추가'}
          </DialogTitle>
          <DialogContent>
            {editingContent && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="서브탭"
                    value={editingContent.subTab}
                    onChange={(e) => setEditingContent({ ...editingContent, subTab: e.target.value })}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="제목"
                    value={editingContent.title}
                    onChange={(e) => setEditingContent({ ...editingContent, title: e.target.value })}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="설명"
                    value={editingContent.description}
                    onChange={(e) => setEditingContent({ ...editingContent, description: e.target.value })}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    콘텐츠 미디어 (선택사항)
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    이미지나 영상을 업로드할 수 있습니다. 미디어 없이도 저장할 수 있습니다.
                  </Alert>
                  {((editingContent.src && editingContent.src.trim() !== '') || (editingContent as any).videoSrc) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        현재 미디어: {(editingContent as any).videoSrc || editingContent.src}
                      </Typography>
                      <MediaPreview 
                        src={(editingContent as any).videoSrc || editingContent.src} 
                        alt="콘텐츠" 
                        title={editingContent.title || '콘텐츠'}
                        type={((editingContent as any).videoSrc || editingContent.src).match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? "video" : "image"}
                        width="100%"
                        height={200}
                        showPreviewButton={true}
                        showTypeChip={true}
                      />
                    </Box>
                  )}
                  <MediaManager
                    onMediaSelect={(media) => {
                      console.log('미디어 선택됨:', media);
                      // videoSrc가 있는 경우 videoSrc를 업데이트, 없으면 src를 업데이트
                      if ((editingContent as any).videoSrc !== undefined) {
                        setEditingContent({ ...editingContent, videoSrc: media.url });
                      } else {
                        setEditingContent({ ...editingContent, src: media.url });
                      }
                    }}
                    onTempUpload={handleTempUpload}
                    onCancelUpload={handleCancelUpload}
                    filterType="all"
                    showUpload={true}
                    showPreview={true}
                    showEdit={false}
                    showDelete={false}
                    multiple={false}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowContentDialog(false)}>
              취소
            </Button>
            <Button onClick={handleSaveContent} variant="contained">
              저장
            </Button>
          </DialogActions>
        </Dialog>

        {/* 성공/실패 알림 */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSuccess(false)}
        >
          <Alert onClose={() => setShowSuccess(false)} severity="success">
            포트폴리오가 성공적으로 저장되었습니다!
          </Alert>
        </Snackbar>

        <Snackbar
          open={showError}
          autoHideDuration={5000}
          onClose={() => setShowError(false)}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}; 